import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DcButton,
  DcDropDown,
  DcInput,
  PixelFactoryContext,
  PixelTable,
} from 'pixel-base'
import {
  createPayment,
  deleteFinancePayment,
  getChartOfAccounts,
  getJournals,
  getPartners,
  getPayments,
  postPaymentDocument,
  reversePaymentDocument,
  updatePayment,
} from 'utils/api-routes-accounts'
import FinanceListActions from '../../components/domain/FinanceListActions'
import FinancePageShell from '../../components/FinancePageShell'
import FinanceStatusBadge from '../../components/FinanceStatusBadge'
import FinanceEntityDrawer from '../../components/foundation/FinanceEntityDrawer'
import { useFinanceObservable } from '../../hooks/useFinanceObservable'
import { Account, Journal, Partner, Payment } from '../../types/finance'
import formatFinanceDate from '../../utils/formatFinanceDate'

const today = new Date().toISOString().split('T')[0]

const defaultForm = {
  id: null,
  version_no: 1,
  payment_direction: 'inbound',
  partner_id: '',
  journal_id: '',
  counterparty_account_id: '',
  payment_date: today,
  currency_code: 'PKR',
  reference: '',
  notes: '',
  amount: 0,
}

const Payments = () => {
  const pixelFactory = React.useContext(PixelFactoryContext) as any
  const navigate = useNavigate()
  const payments = useFinanceObservable<Payment[]>(() => getPayments())
  const partners = useFinanceObservable<Partner[]>(() => getPartners())
  const journals = useFinanceObservable<Journal[]>(() => getJournals())
  const accounts = useFinanceObservable<Account[]>(() => getChartOfAccounts())
  const [isOpen, setIsOpen] = React.useState(false)
  const [form, setForm] = React.useState<any>(defaultForm)

  const reset = () => {
    setForm(defaultForm)
    setIsOpen(false)
  }

  const hydrateForm = (payment: Payment) => {
    setForm({
      ...defaultForm,
      ...payment,
      version_no: payment.version_no || 1,
      payment_date: formatFinanceDate(payment.payment_date) || today,
      reference: payment.reference || '',
      notes: payment.notes || '',
    })
    setIsOpen(true)
  }

  const save = () => {
    const payload = {
      ...form,
      version_no: Number(form.version_no || 1),
      partner_id: Number(form.partner_id),
      journal_id: Number(form.journal_id),
      counterparty_account_id: Number(form.counterparty_account_id),
      amount: Number(form.amount || 0),
    }
    const request = form.id
      ? updatePayment(form.id, payload)
      : createPayment(payload)
    request.subscribe({
      next: () => {
        pixelFactory?.showSuccess?.(
          form.id ? 'Payment updated' : 'Payment created',
        )
        reset()
        payments.refresh()
      },
      error: () => pixelFactory?.showFailure?.('Unable to save payment'),
    })
  }

  const runAction = (request, successMessage: string) =>
    request.subscribe({
      next: () => {
        pixelFactory?.showSuccess?.(successMessage)
        payments.refresh()
      },
      error: () => pixelFactory?.showFailure?.('Payment action failed'),
    })

  return (
    <>
      <FinancePageShell
        title="Payments"
        isLoading={payments.loading}
        primaryAction={{ label: 'New Payment', onClick: () => setIsOpen(true) }}
      >
        <PixelTable
          columns={[
            {
              name: 'Number',
              selector: (row) => row.number || `PAY-${row.id}`,
            },
            { name: 'Direction', selector: (row) => row.payment_direction },
            {
              name: 'Partner',
              selector: (row) => row.partner?.name || row.partner_id || '--',
            },
            {
              name: 'Date',
              selector: (row) => formatFinanceDate(row.payment_date),
            },
            { name: 'Amount', selector: (row) => row.amount ?? '--' },
            { name: 'Open Amount', selector: (row) => row.open_amount ?? '--' },
            {
              name: 'Status',
              selector: (row) => <FinanceStatusBadge status={row.state} />,
            },
            {
              name: 'Actions',
              selector: (row) => {
                const state = (row.state || 'draft').toLowerCase()
                const canEdit = state === 'draft'
                const canPost = state === 'draft'
                const canReverse = state === 'posted'
                const canDelete = state === 'draft'

                return (
                  <FinanceListActions
                    actions={[
                      {
                        key: 'view',
                        label: 'View',
                        tone: 'primary',
                        onClick: () => navigate(`/finance/payments/${row.id}`),
                      },
                      {
                        key: 'edit',
                        label: 'Edit',
                        tone: 'secondary',
                        hidden: !canEdit,
                        onClick: () => hydrateForm(row),
                      },
                      {
                        key: 'post',
                        label: 'Post',
                        tone: 'success',
                        hidden: !canPost,
                        onClick: () =>
                          runAction(
                            postPaymentDocument(row.id, {
                              idempotency_key: `payment-post-${row.id}`,
                            }),
                            'Payment posted',
                          ),
                      },
                      {
                        key: 'reverse',
                        label: 'Reverse',
                        tone: 'warning',
                        hidden: !canReverse,
                        onClick: () =>
                          runAction(
                            reversePaymentDocument(row.id, {
                              idempotency_key: `payment-reverse-${row.id}`,
                            }),
                            'Payment reversed',
                          ),
                      },
                      {
                        key: 'delete',
                        label: 'Delete',
                        tone: 'danger',
                        hidden: !canDelete,
                        onClick: () =>
                          runAction(
                            deleteFinancePayment(row.id),
                            'Payment deleted',
                          ),
                      },
                    ]}
                  />
                )
              },
            },
          ]}
          data={payments.data || []}
          searchable
          pagination
        />
      </FinancePageShell>
      <FinanceEntityDrawer
        isOpen={isOpen}
        onClose={reset}
        title={form.id ? 'Edit Payment' : 'Create Payment'}
      >
        <DcDropDown
          label="Direction"
          value={form.payment_direction}
          options={[
            { value: 'inbound', label: 'Inbound' },
            { value: 'outbound', label: 'Outbound' },
          ]}
          onChange={(e) =>
            setForm({ ...form, payment_direction: e.target.value })
          }
        />
        <DcDropDown
          label="Partner"
          value={form.partner_id}
          options={(partners.data || []).map((partner) => ({
            value: partner.id,
            label: `${partner.code} - ${partner.name}`,
          }))}
          onChange={(e) => setForm({ ...form, partner_id: e.target.value })}
        />
        <DcDropDown
          label="Journal"
          value={form.journal_id}
          options={(journals.data || []).map((journal) => ({
            value: journal.id,
            label: journal.name,
          }))}
          onChange={(e) => setForm({ ...form, journal_id: e.target.value })}
        />
        <DcDropDown
          label="Counterparty Account"
          value={form.counterparty_account_id}
          options={(accounts.data || []).map((account) => ({
            value: account.id,
            label: `${account.code} - ${account.name}`,
          }))}
          onChange={(e) =>
            setForm({ ...form, counterparty_account_id: e.target.value })
          }
        />
        <DcInput
          type="date"
          label="Payment Date"
          value={form.payment_date}
          onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
        />
        <DcInput
          label="Reference"
          value={form.reference}
          onChange={(e) => setForm({ ...form, reference: e.target.value })}
        />
        <DcInput
          label="Notes"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
        <DcInput
          type="number"
          label="Amount"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
        />
        <DcButton onClick={save}>Save Payment</DcButton>
      </FinanceEntityDrawer>
    </>
  )
}

export default Payments
